import { Fragment, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { lineCount, parseMarkdown, type Block, type Inline } from './markdownText';

function inline(parts: Inline[]): ReactNode {
  return parts.map((part, index) => {
    switch (part.kind) {
      case 'strong':
        return <strong key={index}>{part.text}</strong>;
      case 'em':
        return <em key={index}>{part.text}</em>;
      case 'code':
        return <code key={index}>{part.text}</code>;
      case 'link':
        return (
          <a key={index} href={part.href} target="_blank" rel="noreferrer noopener">
            {part.text}
          </a>
        );
      default:
        return <Fragment key={index}>{part.text}</Fragment>;
    }
  });
}

function block(item: Block, index: number): ReactNode {
  switch (item.kind) {
    case 'heading': {
      const Tag = (['h3', 'h4', 'h5'] as const)[item.level - 1] ?? 'h5';
      return <Tag key={index}>{inline(item.content)}</Tag>;
    }
    case 'bullets':
      return (
        <ul key={index}>
          {item.items.map((row, rowIndex) => (
            <li key={rowIndex}>{inline(row)}</li>
          ))}
        </ul>
      );
    case 'numbers':
      return (
        <ol key={index}>
          {item.items.map((row, rowIndex) => (
            <li key={rowIndex}>{inline(row)}</li>
          ))}
        </ol>
      );
    case 'quote':
      return <blockquote key={index}>{inline(item.content)}</blockquote>;
    case 'pre':
      return <pre key={index}>{item.text}</pre>;
    case 'rule':
      return <hr key={index} />;
    default:
      return <p key={index}>{inline(item.content)}</p>;
  }
}

/**
 * A description, formatted, and short until asked otherwise.
 *
 * Collapsed by default because a description can be a page of notes, and the
 * card exists to answer "which part is this" — a page of notes pushes the
 * stock, the photographs and the parameters off the screen. The fold is a
 * height rather than a count of blocks: cutting between blocks would drop a
 * whole list when it happens to be the second one.
 */
export function Markdown({ source, lines = 3 }: { source: string; lines?: number }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const blocks = parseMarkdown(source);
  const long = lineCount(source) > lines;

  return (
    <div className="markdown">
      <div
        className={long && !open ? 'markdown__body markdown__body--clamped' : 'markdown__body'}
        style={long && !open ? { ['--markdown-lines' as string]: lines } : undefined}
      >
        {blocks.map(block)}
      </div>
      {long && (
        <button type="button" className="component-form__scope" onClick={() => setOpen(!open)}>
          {open ? t('common.collapse') : t('common.expand')}
        </button>
      )}
    </div>
  );
}
